import React, { useState, useCallback } from "react";
import { HiArrowUpRight } from "react-icons/hi2";
import { AiFillGithub, AiOutlineTwitter, AiFillInstagram } from "react-icons/ai";
import { FaLinkedinIn } from "react-icons/fa";

import api from "../../lib/api";
import useReveal from "../../hooks/useReveal";
import useSEO, { SITE_URL } from "../../seo/useSEO";
import { breadcrumb } from "../../seo/schemas";
import Turnstile from "../../components/Turnstile";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const BUDGETS = [
  { value: "", label: "Pick a range (optional)" },
  { value: "<5k", label: "Under $5k" },
  { value: "5-15k", label: "$5k – $15k" },
  { value: "15-40k", label: "$15k – $40k" },
  { value: "40k+", label: "$40k+" },
  { value: "retainer", label: "Ongoing / retainer" },
];

const SUBJECTS = [
  "Shopify project",
  "Web app build",
  "API / backend",
  "Performance & SEO",
  "Consulting",
  "Other",
];

export default function Contact() {
  useReveal();
  useSEO({
    title: "Contact — Oguzhan Ozcelik | Full-Stack Developer & Shopify Expert",
    description:
      "Get in touch with Oguzhan Ozcelik for Shopify, web app, API and performance work. Send a message via the contact form.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": `${SITE_URL}/contact#page`,
          url: `${SITE_URL}/contact`,
          name: "Contact — Oguzhan Ozcelik",
          mainEntity: { "@id": `${SITE_URL}/#person` },
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]),
      ],
    },
    jsonLdId: "contact-jsonld",
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: SUBJECTS[0],
    budget: "",
    message: "",
    website: "", // honeypot
  });
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onToken = useCallback((t) => setToken(t || ""), []);
  const onExpire = useCallback(() => setToken(""), []);
  const onTSError = useCallback(() => setToken(""), []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.name.trim().length < 2) return setError("Please enter your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError("Please enter a valid email.");
    if (form.message.trim().length < 10) return setError("Please write a longer message.");
    if (!token) return setError("Please complete the verification widget.");

    setBusy(true);
    try {
      await api.post("/api/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject,
        budget: form.budget,
        message: form.message.trim(),
        website: form.website,
        turnstileToken: token,
      });
      setDone(true);
    } catch (e2) {
      setError(e2?.response?.data?.error || e2.message || "Could not send your message.");
      setToken("");
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch (er) {}
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main>
      <section className="section section--hero">
        <div className="container-x reveal">
          <span className="eyebrow">Contact</span>
          <h1 className="about-hero__title">
            Let's build
            <br />
            <span className="it text-accent">something.</span>
          </h1>
          <p className="lead" style={{ maxWidth: "60ch", marginTop: 24 }}>
            Tell me about your project — Shopify, web app, API, performance,
            consulting. I read every message and reply within a couple of
            business days.
          </p>
        </div>
      </section>

      <section className="section section--tight">
        <div className="container-x">
          <div className="contact-grid">
            <aside className="contact-aside reveal">
              <span className="eyebrow">How I can help</span>
              <h3>
                Shopify, full-stack,
                <br />
                <span className="it text-accent">performance & SEO.</span>
              </h3>
              <p style={{ color: "var(--text-soft)" }}>
                Engagements range from one-off audits to long-term builds.
                Share what you have — even if it's a rough idea — and I'll
                tell you honestly whether I'm the right fit.
              </p>

              <div className="info">
                <div className="info-row">
                  <div className="label">Based in</div>
                  <div className="value">New York, US · Working worldwide</div>
                </div>
                <div className="info-row">
                  <div className="label">Response time</div>
                  <div className="value">Usually within 1–2 business days</div>
                </div>
                <div className="info-row">
                  <div className="label">Find me</div>
                  <ul className="footer__social" style={{ marginTop: 8 }}>
                    <li>
                      <a href="https://github.com/oguzhanozcelik" target="_blank" rel="noreferrer noopener" aria-label="GitHub"><AiFillGithub /></a>
                    </li>
                    <li>
                      <a href="https://www.linkedin.com/in/oguzhanozcelik/" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn"><FaLinkedinIn /></a>
                    </li>
                    <li>
                      <a href="https://twitter.com/oguzhanozcelikk" target="_blank" rel="noreferrer noopener" aria-label="Twitter"><AiOutlineTwitter /></a>
                    </li>
                    <li>
                      <a href="https://www.instagram.com/oguzhanozcelikk" target="_blank" rel="noreferrer noopener" aria-label="Instagram"><AiFillInstagram /></a>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>

            <div className="reveal" style={{ minWidth: 0 }}>
              {done ? (
                <div className="contact-success">
                  <div className="check" aria-hidden="true">✓</div>
                  <h3>Message sent</h3>
                  <p style={{ color: "var(--text-soft)" }}>
                    Thanks for reaching out — I'll get back to you within 1–2
                    business days. Meanwhile, feel free to browse the projects
                    or the blog.
                  </p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={submit} noValidate>
                  <div className="row">
                    <div className="field">
                      <label htmlFor="name">Name</label>
                      <input
                        id="name"
                        className="input"
                        value={form.name}
                        onChange={set("name")}
                        autoComplete="name"
                        required
                      />
                    </div>
                    <div className="field">
                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        type="email"
                        className="input"
                        value={form.email}
                        onChange={set("email")}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="field">
                      <label htmlFor="subject">Subject</label>
                      <select
                        id="subject"
                        className="select"
                        value={form.subject}
                        onChange={set("subject")}
                      >
                        {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label htmlFor="budget">Budget</label>
                      <select
                        id="budget"
                        className="select"
                        value={form.budget}
                        onChange={set("budget")}
                      >
                        {BUDGETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="field">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      className="textarea"
                      rows={6}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="A few sentences on the project, timeline, and what success looks like."
                      style={{ minHeight: 160, fontFamily: "var(--font-body)" }}
                      required
                    />
                  </div>

                  {/* Honeypot */}
                  <div className="honeypot" aria-hidden="true">
                    <label htmlFor="website">Leave this empty</label>
                    <input
                      id="website"
                      type="text"
                      tabIndex="-1"
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>

                  <div className="turnstile-wrap">
                    <Turnstile
                      siteKey={SITE_KEY}
                      onToken={onToken}
                      onExpire={onExpire}
                      onError={onTSError}
                      action="contact"
                    />
                  </div>

                  {error && <div className="error-msg" role="alert">{error}</div>}

                  <div className="submit-row">
                    <small className="legal">
                      Your details are used only to reply. No newsletter, no tracking
                      beyond the site analytics already disclosed.
                    </small>
                    <button type="submit" className="btn btn--lg" disabled={busy || !token}>
                      {busy ? "Sending…" : "Send message"}
                      <span className="arrow"><HiArrowUpRight /></span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
